import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { searchClient, STORE_INDEX } from '../services/algoliaClient';

const STORE_BROWSE_HITS_PER_PAGE = 1000;
const STORE_SEARCH_HITS_PER_PAGE = 100;

const StoreContext = createContext();

export const useStore = () => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
};

export const StoreProvider = ({ children }) => {
  // Initialize state from localStorage if available
  const [selectedStore, setSelectedStore] = useState(() => {
    const saved = localStorage.getItem('selectedStore');
    return saved ? JSON.parse(saved) : null;
  });
  
  const [persona, setPersona] = useState(() => {
    const saved = localStorage.getItem('persona');
    return saved ? JSON.parse(saved) : 'public';
  });
  
  const [storeResults, setStoreResults] = useState([]);
  const [storeQuery, setStoreQuery] = useState('');
  const [loadingStores, setLoadingStores] = useState(false);
  const [selectedStoreData, setSelectedStoreData] = useState(() => {
    const saved = localStorage.getItem('selectedStoreData');
    return saved ? JSON.parse(saved) : null;
  });

  // Save selectedStore to localStorage whenever it changes
  useEffect(() => {
    if (selectedStore !== null) {
      localStorage.setItem('selectedStore', JSON.stringify(selectedStore));
    } else {
      localStorage.removeItem('selectedStore');
      // Only reset selectedStoreData if selectedStore is actually null
      setSelectedStoreData(null);
    }
  }, [selectedStore]);

  // Save persona to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('persona', JSON.stringify(persona));
  }, [persona]);

  // Save selectedStoreData to localStorage whenever it changes
  useEffect(() => {
    if (selectedStoreData !== null) {
      localStorage.setItem('selectedStoreData', JSON.stringify(selectedStoreData));
    } else {
      localStorage.removeItem('selectedStoreData');
    }
  }, [selectedStoreData]);

  // Update selectedStoreData when storeResults change and we have a selectedStore
  useEffect(() => {
    if (selectedStore && storeResults.length > 0 && !selectedStoreData) {
      const foundStore = storeResults.find(s => String(s.store_id) === String(selectedStore));
      if (foundStore) {
        setSelectedStoreData(foundStore);
      }
    }
  }, [selectedStore, storeResults, selectedStoreData]);

  // Algolia store search — empty / whitespace query browses the index (all stores, up to cap)
  const searchStores = useCallback(async (query) => {
    const raw = typeof query === 'string' ? query : '';
    setStoreQuery(raw);

    const q = raw.trim();
    setLoadingStores(true);
    try {
      const response = await searchClient.search([{
        indexName: STORE_INDEX,
        params: {
          query: q,
          hitsPerPage: q ? STORE_SEARCH_HITS_PER_PAGE : STORE_BROWSE_HITS_PER_PAGE,
        }
      }]);
      const hits = response.results[0]?.hits || [];
      setStoreResults(hits);
    } catch (error) {
      console.error('Error searching stores:', error);
      setStoreResults([]);
    } finally {
      setLoadingStores(false);
    }
  }, []);

  // Function to set selected store with its data
  const setSelectedStoreWithData = (storeId, storeData = null) => {
    // Ensure storeId is a string for consistency
    const normalizedStoreId = String(storeId);
    setSelectedStore(normalizedStoreId);
    
    if (storeData) {
      setSelectedStoreData(storeData);
    }
  };

  return (
    <StoreContext.Provider value={{
      selectedStore,
      setSelectedStore: setSelectedStoreWithData,
      selectedStoreData,
      persona,
      setPersona,
      storeResults,
      searchStores,
      storeQuery,
      loadingStores
    }}>
      {children}
    </StoreContext.Provider>
  );
}; 